import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { adminDashboard } from '../../api/admin';
import {
  FiShoppingBag,
  FiClock,
  FiDollarSign,
  FiTrendingUp,
  FiPackage
} from 'react-icons/fi';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  background: #F5F0E8;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const StatIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color};
  font-size: 24px;
  margin-bottom: 16px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #6B5E4E;
  margin-bottom: 8px;
  font-weight: 500;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #1C1511;
`;

const Section = styled.section`
  background: #F5F0E8;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1C1511;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: Georgia, serif;
`;

const ProductList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ProductItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background-color: #FAF8F3;
  border-radius: 8px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #ede8df;
  }
`;

const ProductName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #1C1511;
`;

const ProductSales = styled.span`
  font-size: 14px;
  color: #6B5E4E;
  font-weight: 600;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
`;

const Spinner = styled.div`
  border: 3px solid #E8DDD0;
  border-top: 3px solid #D4A017;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
`;

const TabList = styled.div`
  display: flex;
  gap: 8px;
  border-bottom: 1px solid #E8DDD0;
  margin-bottom: 24px;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 12px 20px;
  background: none;
  border: none;
  border-bottom: 3px solid ${props => (props.$active ? '#D4A017' : 'transparent')};
  color: ${props => (props.$active ? '#1C1511' : '#6B5E4E')};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
`;

type DashboardTab = 'qb' | 'cc';

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const CustomCakesPanel: React.FC = () => {
  const { t } = useTranslation();
  const { data: dashboardData, isLoading, error } = useQuery({
    queryKey: ['adminDashboard'],
    queryFn: adminDashboard.getData,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorMessage>
        {t('admin.dashboard.loadError')}
      </ErrorMessage>
    );
  }

  return (
    <>
      <StatsGrid>
        <StatCard>
          <StatIcon color="#D4A017">
            <FiShoppingBag />
          </StatIcon>
          <StatLabel>{t('admin.dashboard.totalOrders')}</StatLabel>
          <StatValue>{dashboardData?.total_orders || 0}</StatValue>
        </StatCard>

        <StatCard>
          <StatIcon color="#f59e0b">
            <FiClock />
          </StatIcon>
          <StatLabel>{t('admin.dashboard.pendingOrders')}</StatLabel>
          <StatValue>{dashboardData?.pending_orders || 0}</StatValue>
        </StatCard>

        <StatCard>
          <StatIcon color="#10b981">
            <FiDollarSign />
          </StatIcon>
          <StatLabel>{t('admin.dashboard.totalRevenue')}</StatLabel>
          <StatValue>{formatCurrency(dashboardData?.total_revenue || 0)}</StatValue>
        </StatCard>

        <StatCard>
          <StatIcon color="#ef4444">
            <FiTrendingUp />
          </StatIcon>
          <StatLabel>{t('admin.dashboard.todaysOrders')}</StatLabel>
          <StatValue>{dashboardData?.today_orders || 0}</StatValue>
        </StatCard>
      </StatsGrid>

      <Section>
        <SectionTitle>
          <FiPackage />
          {t('admin.dashboard.topProducts')}
        </SectionTitle>
        <ProductList>
          {dashboardData?.top_products?.length ? (
            dashboardData.top_products.map((product) => (
              <ProductItem key={product.id}>
                <ProductName>{product.name}</ProductName>
                <ProductSales>{t('admin.dashboard.soldSuffix', { count: product.total_sold })}</ProductSales>
              </ProductItem>
            ))
          ) : (
            <ProductItem>
              <ProductName>{t('admin.dashboard.noProductsSold')}</ProductName>
              <ProductSales>-</ProductSales>
            </ProductItem>
          )}
        </ProductList>
      </Section>
    </>
  );
};

const QuickBitesPanel: React.FC = () => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery({
    queryKey: ['adminDashboardQuickBites'],
    queryFn: adminDashboard.getQuickBitesData,
    refetchInterval: 30000,
  });

  if (isLoading) {
    return (
      <LoadingContainer role="status" aria-label={t('admin.dashboard.qb.loading')}>
        <Spinner />
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorMessage>
        {t('admin.dashboard.qb.loadError')}
      </ErrorMessage>
    );
  }

  return (
    <>
      <StatsGrid>
        <StatCard data-testid="qb-stat-total">
          <StatIcon color="#D4A017">
            <FiShoppingBag />
          </StatIcon>
          <StatLabel>{t('admin.dashboard.qb.totalOrders')}</StatLabel>
          <StatValue>{data?.total_orders || 0}</StatValue>
        </StatCard>

        <StatCard data-testid="qb-stat-pending">
          <StatIcon color="#f59e0b">
            <FiClock />
          </StatIcon>
          <StatLabel>{t('admin.dashboard.qb.pendingActive')}</StatLabel>
          <StatValue>{data?.pending_orders || 0} / {data?.active_orders || 0}</StatValue>
        </StatCard>

        <StatCard data-testid="qb-stat-today">
          <StatIcon color="#ef4444">
            <FiTrendingUp />
          </StatIcon>
          <StatLabel>{t('admin.dashboard.qb.todaysOrders')}</StatLabel>
          <StatValue>{data?.today_orders || 0}</StatValue>
        </StatCard>

        <StatCard data-testid="qb-stat-revenue">
          <StatIcon color="#10b981">
            <FiDollarSign />
          </StatIcon>
          <StatLabel>{t('admin.dashboard.qb.revenue')}</StatLabel>
          <StatValue>{formatCurrency(data?.total_revenue || 0)}</StatValue>
        </StatCard>
      </StatsGrid>

      <Section data-testid="qb-top-products">
        <SectionTitle>
          <FiPackage />
          {t('admin.dashboard.qb.topProducts')}
        </SectionTitle>
        <ProductList>
          {data?.top_products?.length ? (
            data.top_products.slice(0, 5).map((product) => (
              <ProductItem key={product.id}>
                <ProductName>{product.name}</ProductName>
                <ProductSales>{t('admin.dashboard.qb.soldSuffix', { count: product.total_sold })}</ProductSales>
              </ProductItem>
            ))
          ) : (
            <ProductItem>
              <ProductName>{t('admin.dashboard.qb.noProducts')}</ProductName>
              <ProductSales>-</ProductSales>
            </ProductItem>
          )}
        </ProductList>
      </Section>
    </>
  );
};

const AdminDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<DashboardTab>('qb');

  return (
    <Container>
      <TabList role="tablist" aria-label={t('admin.dashboard.tabs.ariaLabel')}>
        <Tab
          type="button"
          role="tab"
          id="dashboard-tab-qb"
          aria-selected={activeTab === 'qb'}
          aria-controls="dashboard-panel-qb"
          $active={activeTab === 'qb'}
          data-testid="dashboard-tab-qb"
          onClick={() => setActiveTab('qb')}
        >
          {t('admin.dashboard.tabs.qb')}
        </Tab>
        <Tab
          type="button"
          role="tab"
          id="dashboard-tab-cc"
          aria-selected={activeTab === 'cc'}
          aria-controls="dashboard-panel-cc"
          $active={activeTab === 'cc'}
          data-testid="dashboard-tab-cc"
          onClick={() => setActiveTab('cc')}
        >
          {t('admin.dashboard.tabs.cc')}
        </Tab>
      </TabList>

      {activeTab === 'qb' ? (
        <div role="tabpanel" id="dashboard-panel-qb" aria-labelledby="dashboard-tab-qb" data-testid="dashboard-panel-qb">
          <QuickBitesPanel />
        </div>
      ) : (
        <div role="tabpanel" id="dashboard-panel-cc" aria-labelledby="dashboard-tab-cc" data-testid="dashboard-panel-cc">
          <CustomCakesPanel />
        </div>
      )}
    </Container>
  );
};

export default AdminDashboard;